import React, { useRef, useMemo, useState, useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';
import { PieChart } from 'echarts/charts';
import {
    GridComponent,
    LegendComponent,
    TitleComponent,
    TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useAppContext } from '../../../../../providers/AppProvider';
import { Spinner } from 'react-bootstrap';

// Carga diferida del componente ReactEchart con prioridad
const ReactEchart = React.lazy(() =>
    // Añadir una pequeña demora para evitar conflictos con otros renderizados
    new Promise(resolve => {
        // Priorizar el renderizado de la UI principal primero
        setTimeout(() => {
            resolve(import('../../../../../components/common/ReactEchart'));
        }, 100);
    })
);

// Registrar solo los componentes necesarios
echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    PieChart,
    CanvasRenderer,
    LegendComponent
]);

// Extraer la función getOptions fuera del componente para evitar recreaciones
const getOptions = (data, getThemeColor) => {
    if (!getThemeColor || typeof getThemeColor !== 'function') {
        console.warn("getThemeColor is not a function or is undefined");
        return {};
    }

    return {
        tooltip: {
            trigger: 'item',
            confine: true,
            padding: [7, 10],
            backgroundColor: '#fff',
            borderColor: '#ccc',
            textStyle: { color: '#000' },
            borderWidth: 1,
            transitionDuration: 0,
            formatter: function (params) {
                // 🔧 MEJORA: Validación de params antes de acceder a propiedades
                if (!params || !params.data) {
                    return 'Datos no disponibles';
                }
                const count = params.data.target || 0;
                const percent = params.percent || 0;
                const name = params.data.name || 'Sin nombre';
                return `<strong>${name}:</strong> ${percent}% (${count})`;
            }
        },
        legend: { show: false },
        animation: false, // Desactivar animación para mejorar rendimiento
        series: [
            {
                name: 'Alertas',
                type: 'pie',
                radius: ['100%', '67%'],
                avoidLabelOverlap: false,
                itemStyle: {
                    borderWidth: 2,
                    borderColor: '#fff'
                },
                label: {
                    show: false,
                    formatter: '{b}: {d}%'
                },
                emphasis: {
                    scale: false
                },
                labelLine: { show: false },
                data: data.map(alert => ({
                    value: alert.amount || 0,
                    name: alert.title || 'Sin nombre',
                    target: alert.target || 0,
                    itemStyle: { color: alert.color || '#CCCCCC' },
                    alertType: alert.title || 'Desconocido'
                }))
            }
        ]
    };
};

// Estados de carga más granulares para mejor feedback
const LOADING_STATES = {
    INITIAL: 'initial',   // Estado inicial
    DATA_READY: 'data_ready',   // Datos listos pero gráfico aún cargando
    RENDERING: 'rendering',     // Renderizando el gráfico
    COMPLETED: 'completed'      // Todo completo
};

const AlertasDetonadasChart = ({ data, onSelectAlertType, total }) => {
    const chartRef = useRef(null);
    const { getThemeColor } = useAppContext() || {};
    const [loadingState, setLoadingState] = useState(LOADING_STATES.INITIAL);
    const [lastRenderTime, setLastRenderTime] = useState(0);

    // Limitar el número máximo de segmentos para mejorar rendimiento
    const MAX_SEGMENTS = 6; // Reducido de 8 a 6 para mejor rendimiento visual

    const chartData = useMemo(() => {
        // 🔧 MEJORA: Validación robusta de datos antes de procesarlos
        if (!data || !Array.isArray(data) || data.length === 0) {
            console.warn('AlertasDetonadasChart: Datos vacíos o inválidos');
            return [];
        }

        // Validar que cada elemento tenga las propiedades necesarias
        const validData = data.filter(item =>
            item &&
            typeof item.title === 'string' &&
            typeof item.target === 'number' &&
            typeof item.amount === 'number' &&
            typeof item.color === 'string'
        );

        if (validData.length !== data.length) {
            console.warn(`AlertasDetonadasChart: Se filtraron ${data.length - validData.length} elementos inválidos`);
        }

        // Si hay pocos datos, usarlos directamente
        if (validData.length <= MAX_SEGMENTS) {
            return validData;
        }

        // Si hay muchos tipos de alertas, agrupar las menos frecuentes como "Otros"
        // Ordenar por cantidad (target)
        const sortedData = [...validData].sort((a, b) => b.target - a.target);

        // Tomar los primeros MAX_SEGMENTS-1 elementos
        const topData = sortedData;//.slice(0, MAX_SEGMENTS - 1);
        /*
                // Agrupar el resto como "Otros"
                const otherData = sortedData.slice(MAX_SEGMENTS - 1);
                const otherTotal = otherData.reduce((sum, item) => sum + item.target, 0);
                const otherAmount = total > 0 ? Number(((otherTotal / total) * 100).toFixed(1)) : 0;
                    {
                        title: 'Otros',
                        target: otherTotal,
                        amount: otherAmount,
                        color: '#CCCCCC'
                    }
                */
        return [
            ...topData,
        ];
    }, [data, MAX_SEGMENTS]); // Dependencia corregida

    // Memoizar las opciones del gráfico para evitar recálculos
    const chartOptions = useMemo(() =>
        getOptions(chartData, getThemeColor),
        [chartData, getThemeColor]
    );

    // Detectar cuando los datos están listos
    useEffect(() => {
        if (data && data.length > 0 && loadingState === LOADING_STATES.INITIAL) {
            setLoadingState(LOADING_STATES.DATA_READY);
        }
    }, [data, loadingState]);

    // Prevenir actualizaciones demasiado frecuentes (throttling)
    useEffect(() => {
        if (loadingState === LOADING_STATES.DATA_READY) {
            const now = Date.now();
            // Solo actualizar si han pasado al menos 300ms desde la última renderización
            if (now - lastRenderTime > 300) {
                setLoadingState(LOADING_STATES.RENDERING);
                setLastRenderTime(now);
                // Programar el cambio a COMPLETED después de un breve tiempo
                const timer = setTimeout(() => {
                    setLoadingState(LOADING_STATES.COMPLETED);
                }, 100);
                return () => clearTimeout(timer);
            }
        }
    }, [loadingState, lastRenderTime]);

    // Eventos memorizados para evitar recreaciones en cada render
    const events = useMemo(() => ({
        click: (params) => {
            // 🔧 MEJORA: Validación robusta antes de manejar el evento click
            if (!params || !params.data || !params.data.alertType) {
                console.warn('AlertasDetonadasChart: Evento click sin datos válidos');
                return;
            }

            if (params.data.alertType !== 'Otros') {
                onSelectAlertType(params.data.alertType);
            }
        }
    }), [onSelectAlertType]);

    // Renderizar un placeholder según el estado de carga
    if (loadingState === LOADING_STATES.INITIAL) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '12.5rem' }}>
                <Spinner animation="border" variant="primary" size="sm" />
                <span className="ms-2">Inicializando gráfico...</span>
            </div>
        );
    }

    // Renderizar una versión esqueleto mientras los datos están listos pero aún no se completa
    if (loadingState === LOADING_STATES.DATA_READY) {
        return (
            <div className="position-relative py-2" style={{ height: '12.5rem' }}>
                <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <p className="mb-0 mt-2">Preparando visualización...</p>
                    </div>
                </div>
                <div className="position-absolute top-50 start-50 translate-middle text-center">
                    <p className="fs-10 mb-0 text-400 font-sans-serif fw-medium">Total</p>
                    <p className="fs-6 mb-0 font-sans-serif fw-medium mt-n2">{total}</p>
                </div>
            </div>
        );
    }

    // 🔧 MEJORA CRÍTICA: No renderizar el gráfico si no hay datos válidos
    if (!chartData || chartData.length === 0) {
        return (
            <div className="position-relative py-2" style={{ height: '12.5rem' }}>
                <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="text-center">
                        <p className="mb-0 text-muted">No hay datos para mostrar</p>
                    </div>
                </div>
                <div className="position-absolute top-50 start-50 translate-middle text-center">
                    <p className="fs-10 mb-0 text-400 font-sans-serif fw-medium">Total</p>
                    <p className="fs-6 mb-0 font-sans-serif fw-medium mt-n2">{total || 0}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="position-relative py-2">
            <Suspense fallback={
                <div className="d-flex justify-content-center align-items-center" style={{ height: '12.5rem' }}>
                    <Spinner animation="border" variant="primary" size="sm" />
                    <span className="ms-2">Cargando biblioteca de gráficos...</span>
                </div>
            }>
                {data && data.length === 1 ? (<ReactEchart
                    echarts={echarts}
                    option={getOptions(chartData, getThemeColor)}
                    onEvents={events}
                    style={{ height: '18.5rem' }}
                    opts={{
                        renderer: 'canvas',
                        devicePixelRatio: window.devicePixelRatio || 1
                    }}
                />) : (
                    <ReactEchart
                        ref={chartRef}
                        echarts={echarts}
                        option={chartOptions}
                        onEvents={events}
                        style={{ height: '18.5rem' }}
                        notMerge={true}
                        lazyUpdate={true}
                        opts={{
                            renderer: 'canvas',
                            devicePixelRatio: window.devicePixelRatio || 1
                        }}
                    />
                )
                }
            </Suspense>
            <div className="position-absolute top-50 start-50 translate-middle text-center">
                <p className="fs-10 mb-0 text-400 font-sans-serif fw-medium">Total</p>
                <p className="fs-6 mb-0 font-sans-serif fw-medium mt-n2">{total}</p>
            </div>
        </div>
    );
};

AlertasDetonadasChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            amount: PropTypes.number.isRequired,
            target: PropTypes.number.isRequired,
            color: PropTypes.string.isRequired
        })
    ).isRequired,
    onSelectAlertType: PropTypes.func.isRequired,
    total: PropTypes.number.isRequired
};

// Implementación avanzada de memoización con comparación profunda
const arePropsEqual = (prevProps, nextProps) => {
    // Verificar si cambiaron properties escalares
    if (prevProps.total !== nextProps.total) return false;

    // Verificar si cambió la función callback (comparación por referencia)
    if (prevProps.onSelectAlertType !== nextProps.onSelectAlertType) return false;

    // Si la cantidad de alertas es diferente, definitivamente son diferentes
    if (prevProps.data.length !== nextProps.data.length) return false;

    // Comparación profunda simplificada para evitar re-renderizados innecesarios
    // Solo consideramos cambios en title, target y amount que afectan a la visualización
    const prevDataString = JSON.stringify(
        prevProps.data.map(item => ({
            title: item.title,
            target: item.target,
            amount: item.amount
        }))
    );

    const nextDataString = JSON.stringify(
        nextProps.data.map(item => ({
            title: item.title,
            target: item.target,
            amount: item.amount
        }))
    );

    return prevDataString === nextDataString;
};

// Exportar componente con memoización avanzada
export default React.memo(AlertasDetonadasChart, arePropsEqual);